import { useState, useEffect } from "react";
import { X, Loader2, CheckCircle2, Calendar, IndianRupee } from "lucide-react";
import api from "../api/axios";

/**
 * ConfirmJoiningModal + ConfirmJoiningButton
 * ---------------------------------------------------------------
 * Drops into EmployerOffers.jsx. Shows a "Confirm Joining" action
 * once an offer's status is 'accepted', pre-fills from that offer,
 * and posts to JoiningController@store via the shared `api` instance
 * (so auth headers/baseURL match the rest of the app).
 *
 * POST /joinings expects:
 *   job_application_id  required | exists:job_applications,id
 *   offer_id            nullable | exists:offers,id
 *   joining_date         required | date
 *   salary               nullable | numeric | min:0
 *
 * Usage inside EmployerOffers.jsx's actions cell:
 *
 *   <ConfirmJoiningButton offer={offer} onConfirmed={loadOffers} />
 */

const COLORS = { blue: "#2563EB", orange: "#F59E0B", dark: "#0D1B3D" };

function formatDateInput(dateStr) {
  if (!dateStr) return "";
  return dateStr.split("T")[0];
}

function ConfirmJoiningModal({ offer, onClose, onConfirmed }) {
  const [joiningDate, setJoiningDate] = useState(formatDateInput(offer.joining_date));
  const [salary, setSalary] = useState(offer.salary ?? "");
  const [submitting, setSubmitting] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});
  const [formError, setFormError] = useState("");
  const [success, setSuccess] = useState(null);

  useEffect(() => {
    function onEsc(e) {
      if (e.key === "Escape") onClose();
    }
    document.addEventListener("keydown", onEsc);
    return () => document.removeEventListener("keydown", onEsc);
  }, [onClose]);

  function validate() {
    const errs = {};
    if (!joiningDate) errs.joining_date = "Joining date is required.";
    if (salary !== "" && (isNaN(Number(salary)) || Number(salary) < 0)) {
      errs.salary = "Salary must be a positive number.";
    }
    setFieldErrors(errs);
    return Object.keys(errs).length === 0;
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setFormError("");
    if (!validate()) return;

    setSubmitting(true);
    try {
      const res = await api.post("/joinings", {
        job_application_id: offer.job_application_id,
        offer_id: offer.id,
        joining_date: joiningDate,
        salary: salary === "" ? null : Number(salary),
      });
      setSuccess(res.data);
      onConfirmed(res.data);
    } catch (err) {
      const status = err.response?.status;
      const body = err.response?.data;

      if (status === 422) {
        const backendErrors = {};
        Object.entries(body?.errors ?? {}).forEach(([k, v]) => (backendErrors[k] = v[0]));
        setFieldErrors(backendErrors);
        setFormError("Please fix the highlighted fields.");
      } else if (status === 403) {
        setFormError(body?.message || "This application doesn't belong to your company.");
      } else {
        setFormError(body?.message || "Couldn't confirm this joining.");
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4"
      onMouseDown={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="w-full max-w-md overflow-hidden rounded-xl bg-white shadow-2xl">
        <div className="flex items-center justify-between px-6 py-4" style={{ backgroundColor: COLORS.dark }}>
          <h2 className="text-base font-semibold text-white">
            {success ? "Joining confirmed" : "Confirm Joining"}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="rounded-md p-1 text-gray-300 hover:bg-white/10 hover:text-white"
            aria-label="Close"
          >
            <X size={18} />
          </button>
        </div>

        {success ? (
          <div className="px-6 py-8 text-center">
            <CheckCircle2 className="mx-auto mb-3" style={{ color: "#16A34A" }} size={32} />
            <p className="text-sm text-gray-700">
              <span className="font-medium">{offer.candidate_name}</span> is scheduled to join on{" "}
              {new Date(success.joining_date).toLocaleDateString()}.
            </p>
            <button
              type="button"
              onClick={onClose}
              className="mt-5 rounded-md px-4 py-2 text-sm font-medium text-white"
              style={{ backgroundColor: COLORS.blue }}
            >
              Done
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="px-6 py-5">
            <p className="mb-4 text-sm text-gray-500">
              Confirming will schedule a joining record for this accepted offer. You can adjust the
              date or salary below if it changed since the offer was sent.
            </p>

            <div className="mb-4 rounded-md border border-gray-200 bg-gray-50 px-3.5 py-2.5">
              <p className="text-sm font-medium" style={{ color: COLORS.dark }}>
                {offer.candidate_name}
              </p>
              <p className="text-xs text-gray-500">Offer #{offer.id} · accepted</p>
            </div>

            <div className="space-y-4">
              <div>
                <label className="mb-1.5 block text-sm font-medium text-gray-700">
                  Joining date <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <Calendar size={16} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input
                    type="date"
                    value={joiningDate}
                    onChange={(e) => setJoiningDate(e.target.value)}
                    className="w-full rounded-md border border-gray-300 bg-white py-2.5 pl-9 pr-3 text-sm text-gray-900 focus:border-gray-500 focus:outline-none focus:ring-2 focus:ring-gray-200"
                  />
                </div>
                {fieldErrors.joining_date && (
                  <p className="mt-1.5 text-xs text-red-500">{fieldErrors.joining_date}</p>
                )}
              </div>

              <div>
                <label className="mb-1.5 block text-sm font-medium text-gray-700">Salary (₹ / year)</label>
                <div className="relative">
                  <IndianRupee size={16} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={salary}
                    onChange={(e) => setSalary(e.target.value)}
                    className="w-full rounded-md border border-gray-300 bg-white py-2.5 pl-9 pr-3 text-sm text-gray-900 focus:border-gray-500 focus:outline-none focus:ring-2 focus:ring-gray-200"
                  />
                </div>
                {fieldErrors.salary && <p className="mt-1.5 text-xs text-red-500">{fieldErrors.salary}</p>}
              </div>
            </div>

            {formError && (
              <div className="mt-4 rounded-md bg-red-50 px-3.5 py-2.5 text-sm text-red-600">{formError}</div>
            )}

            <div className="mt-6 flex justify-end gap-3">
              <button
                type="button"
                onClick={onClose}
                className="rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={submitting}
                className="flex items-center gap-2 rounded-md px-4 py-2 text-sm font-medium text-white disabled:cursor-not-allowed disabled:opacity-60"
                style={{ backgroundColor: COLORS.blue }}
              >
                {submitting && <Loader2 size={16} className="animate-spin" />}
                {submitting ? "Confirming…" : "Confirm Joining"}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

export default function ConfirmJoiningButton({ offer, onConfirmed = () => {} }) {
  const [open, setOpen] = useState(false);

  const alreadyConfirmed = Boolean(offer.joining || offer.joining_id);
  if (offer.status !== "accepted" || alreadyConfirmed) return null;

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="text-sm font-medium hover:underline"
        style={{ color: COLORS.blue }}
      >
        Confirm Joining
      </button>

      {open && (
        <ConfirmJoiningModal
          offer={offer}
          onClose={() => setOpen(false)}
          onConfirmed={(joining) => {
            onConfirmed(joining);
            setOpen(false);
          }}
        />
      )}
    </>
  );
}